import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { KeyRound, ArrowLeft, Eye, EyeOff, Check } from "lucide-react";

export default function ChangePassword() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const [saving, setSaving] = useState(false);
  const nav = useNavigate();

  const rules = [
    { label: "At least 12 characters", ok: next.length >= 12 },
    { label: "Different from current", ok: next.length > 0 && next !== current },
    { label: "Confirmation matches", ok: confirm.length > 0 && next === confirm },
  ];
  const allOk = rules.every((r) => r.ok);

  const submit = async (e) => {
    e.preventDefault();
    if (!current || !next || !confirm) return toast.error("All fields are required");
    if (!allOk) return toast.error("Please meet all requirements");
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password updated");
      nav("/");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to change password");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="h-full overflow-y-auto p-8" data-testid="change-password-page">
      <button onClick={() => nav(-1)} className="inline-flex items-center gap-1.5 text-xs text-white/50 hover:text-white transition-colors mb-4" data-testid="cp-back-btn">
        <ArrowLeft className="w-3.5 h-3.5" /> Back
      </button>

      <div className="max-w-md">
        <div className="w-12 h-12 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center mb-4">
          <KeyRound className="w-5 h-5 text-white/80" />
        </div>
        <div className="text-eyebrow mb-2">Account Security</div>
        <h1 className="font-display text-3xl font-semibold tracking-tight">Change password</h1>
        <p className="text-white/50 mt-2 text-sm">You'll be signed in with your existing session — the next login will use the new password.</p>

        <form onSubmit={submit} className="mt-8 space-y-5" data-testid="cp-form">
          <PasswordField
            id="current" label="Current password" value={current} setValue={setCurrent}
            show={showCurrent} setShow={setShowCurrent} testid="cp-current-input"
          />
          <PasswordField
            id="next" label="New password" value={next} setValue={setNext}
            show={showNext} setShow={setShowNext} testid="cp-new-input"
          />
          <PasswordField
            id="confirm" label="Confirm new password" value={confirm} setValue={setConfirm}
            show={showNext} setShow={setShowNext} testid="cp-confirm-input"
          />

          <div className="p-4 rounded-lg border border-white/10 bg-white/[0.03] space-y-2">
            {rules.map((r, i) => (
              <div key={i} className={`flex items-center gap-2 text-xs ${r.ok ? "text-emerald-400" : "text-white/50"}`}>
                <Check className={`w-3.5 h-3.5 ${r.ok ? "opacity-100" : "opacity-30"}`} />
                {r.label}
              </div>
            ))}
          </div>

          <Button
            type="submit"
            disabled={saving || !allOk}
            data-testid="cp-submit-btn"
            className="w-full h-11 rounded-full bg-white text-black hover:bg-white/90 font-medium disabled:opacity-40"
          >
            {saving ? "Updating…" : "Update password"}
          </Button>
        </form>
      </div>
    </div>
  );
}

function PasswordField({ id, label, value, setValue, show, setShow, testid }) {
  return (
    <div>
      <Label htmlFor={id} className="text-white/70 text-xs uppercase tracking-wider">{label}</Label>
      <div className="relative mt-1.5">
        <Input
          id={id}
          type={show ? "text" : "password"}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          data-testid={testid}
          className="bg-white/5 border-white/10 pr-10 h-11"
          autoComplete="new-password"
        />
        <button
          type="button"
          onClick={() => setShow(!show)}
          className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded hover:bg-white/10 text-white/50 hover:text-white"
          tabIndex={-1}
        >
          {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
}
